import React, { useState, useEffect } from 'react';
import { message, Tooltip } from 'antd';
import { isExtensionEnvironment, getExtensionVersion, versionToNumber } from '../../utils/extensionUtils';
import { get } from '../../services/apiClient';
import languageManager from '../../utils/languageManager';
import { LANGUAGES } from '../../config/languageConfig';
import ExtensionInstallGuide from '../popup/ExtensionInstallGuide';
import FontIcon from '../common/FontIcon';
import logger from '../../utils/logger';
import './TopRightActions.css';

const TopRightActions = () => {
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showExtensionMenu, setShowExtensionMenu] = useState(false);
  const [extensionInfo, setExtensionInfo] = useState(null);
  const [extensionModalVisible, setExtensionModalVisible] = useState(false);
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(languageManager.getCurrentLanguage());
  const [languageKey, setLanguageKey] = useState(0); // 用于触发重新渲染

  // 判断是否是扩展程序环境
  const isExtension = isExtensionEnvironment();
  
  // 扩展程序入口是否显示（只在网页版显示）
  const showExtensionButton = !isExtension;

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = (language) => {
      setCurrentLanguage(language);
      setLanguageKey(prev => prev + 1);
    };
    
    const unsubscribe = languageManager.addListener(handleLanguageChange);
    
    // 也监听 window 事件
    const handleWindowLanguageChange = (event) => {
      const { language } = event.detail || {};
      if (language) {
        setCurrentLanguage(language);
        setLanguageKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('languageChanged', handleWindowLanguageChange);
    
    return () => {
      unsubscribe();
      window.removeEventListener('languageChanged', handleWindowLanguageChange);
    };
  }, []);

  // 切换语言
  const handleLanguageToggle = (lang) => {
    languageManager.switchLanguage(lang);
    setShowLanguageMenu(false);
    setCurrentLanguage(lang);
  };

  // 获取最新扩展程序信息
  const fetchLatestExtension = async () => {
    setExtensionLoading(true);
    try {
      const currentVersion = getExtensionVersion();
      const currentVersionNumber = versionToNumber(currentVersion);
      
      logger.log('[TopRightActions] 当前版本号:', currentVersion, '转换为数字:', currentVersionNumber);
      
      const data = await get('/admin/extensions/check-update', {
        params: { currentVersion: currentVersionNumber }
      });
      
      if (data.success && data.data.hasUpdate) {
        setExtensionInfo(data.data.updateInfo);
        setExtensionModalVisible(true);
      } else {
        message.info(languageManager.t('common.currentVersion'));
      }
    } catch (error) {
      console.error('获取扩展程序信息失败:', error);
      message.error(languageManager.t('common.extensionInfoFetchFailed'));
    } finally {
      setExtensionLoading(false);
    }
  };

  // 下载扩展程序
  const downloadExtension = async () => {
    if (!extensionInfo) return;
    
    try {
      const data = await get(`/admin/extensions/${extensionInfo._id}/download`);
      
      if (data.success) {
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        message.success(languageManager.t('common.downloadStarted'));
        setExtensionModalVisible(false);
      } else {
        message.error(languageManager.t('common.downloadFailed') + ': ' + (data.message || '未知错误'));
      }
    } catch (error) {
      console.error('下载扩展程序失败:', error);
      message.error(languageManager.t('common.downloadFailed'));
    }
  };

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.top-right-actions')) {
        setShowLanguageMenu(false);
        setShowExtensionMenu(false);
      }
    };

    if (showLanguageMenu || showExtensionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageMenu, showExtensionMenu]);

  return (
    <>
      <div className={`top-right-actions ${showExtensionButton ? 'has-extension' : ''}`}>
        {/* 中英文切换按钮 - 在扩展程序入口左边 */}
        <div 
          className={`top-right-action-item language-toggle ${showExtensionButton ? 'hover-only' : ''}`}
        >
          {/* 统一的容器处理鼠标事件，包裹按钮和菜单 */}
          <div
            className="language-toggle-container"
            onMouseEnter={() => {
              // 鼠标进入容器时显示语言菜单
              setShowLanguageMenu(true);
            }}
            onMouseLeave={() => {
              // 鼠标离开容器时关闭菜单
              setShowLanguageMenu(false);
            }}
          >
            <button
              className="action-button language-button"
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            >
              <div className="language-icon">
                <span className="lang-en">En</span>
                <span className="lang-zh">中</span>
              </div>
            </button>
            
            {/* 语言选择下拉菜单 */}
            {showLanguageMenu && (
              <div className="language-dropdown">
              <div 
                className={`language-option ${currentLanguage === LANGUAGES.ZH ? 'active' : ''}`}
                onClick={() => handleLanguageToggle(LANGUAGES.ZH)}
              >
                <span className="flag-icon">🇨🇳</span>
                <span className="language-name">简体中文</span>
                {currentLanguage === LANGUAGES.ZH && <span className="check-icon">✓</span>}
              </div>
              <div 
                className={`language-option ${currentLanguage === LANGUAGES.EN ? 'active' : ''}`}
                onClick={() => handleLanguageToggle(LANGUAGES.EN)}
              >
                <span className="flag-icon">🇺🇸</span>
                <span className="language-name">English</span>
                {currentLanguage === LANGUAGES.EN && <span className="check-icon">✓</span>}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* 扩展程序下载入口（只在网页版显示） */}
        {showExtensionButton && (
          <div className="top-right-action-item extension-button">
            <Tooltip 
              title={languageManager.t('settings.extensionTooltip')}
              placement="bottomRight"
              color="#ffffff"
              overlayInnerStyle={{
                backgroundColor: '#ffffff',
                color: '#000000'
              }}
            >
              <button
                className="action-button extension-button-inner"
                onClick={fetchLatestExtension}
                disabled={extensionLoading}
              >
                <FontIcon 
                  name="puzzle-piece" 
                  size="18px" 
                  style={{ color: '#ffffff' }}
                />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* 扩展程序安装指导弹窗 */}
      <ExtensionInstallGuide
        visible={extensionModalVisible}
        onCancel={() => setExtensionModalVisible(false)}
        onDownload={downloadExtension}
        extensionInfo={extensionInfo}
      />
    </>
  );
};

export default TopRightActions;

